/**
 * Trailer Ranking — pure helpers for the Trailer Ranking tab on /curated.
 *
 * `rows` is the Explore list (at most N); `pool` is every other eligible series,
 * i.e. out of Explore. Save sends both: every series it loaded comes back either
 * ranked or removed.
 */

/**
 * A typed rank moves the row there, like a drag: the rest shift, so two rows can
 * never share a rank. Out-of-range ranks clamp to 1..N; anything that isn't a
 * whole number leaves the list as is.
 */
export const moveToRank = (rows, seriesId, raw) => {
    const from = rows.findIndex((row) => row.seriesId === seriesId);
    const value = String(raw ?? '').trim();
    if (from === -1 || !/^-?\d+$/.test(value)) return rows;
    const to = Math.min(Math.max(Number(value), 1), rows.length) - 1;
    if (to === from) return rows;
    const next = rows.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
};

/**
 * Adds a pool series at `position` (1-based, clamped to 1..rows+1). In a full list
 * the last row falls out into the pool. Returns { rows, pool, pushedOut }.
 */
export const addAt = (rows, pool, seriesId, position, limit) => {
    const picked = pool.find((item) => item.seriesId === seriesId);
    if (!picked) return { rows, pool, pushedOut: null };
    const index = Math.min(Math.max(Number(position) || 1, 1), rows.length + 1) - 1;
    const nextRows = rows.slice();
    nextRows.splice(index, 0, { ...picked, added: true, removedNow: false });
    let nextPool = pool.filter((item) => item.seriesId !== seriesId);
    let pushedOut = null;
    if (nextRows.length > limit) {
        pushedOut = nextRows.pop();
        nextPool = [{ ...pushedOut, added: false, removedNow: true }, ...nextPool];
    }
    return { rows: nextRows, pool: nextPool, pushedOut };
};

/** Takes a row out of Explore. Nothing moves up to fill the slot. */
export const removeRow = (rows, pool, seriesId) => {
    const row = rows.find((item) => item.seriesId === seriesId);
    if (!row) return { rows, pool };
    return {
        rows: rows.filter((item) => item.seriesId !== seriesId),
        pool: [{ ...row, added: false, removedNow: true }, ...pool],
    };
};

/** The PUT payload: the Explore list in order, and everything else as removed. */
export const buildSavePayload = (rows, pool) => ({
    series_ids: rows.map((row) => row.seriesId),
    removed_ids: pool.map((item) => item.seriesId),
});
